import { useCallback, useEffect, useState, type MutableRefObject, type ReactNode } from 'react';
import { useDropzone, type DropEvent } from 'react-dropzone';
import { Upload } from 'lucide-react';
import { useUploadFile } from '../../hooks/queries/useDossierFiles';
import { useAuthStore } from '../../store/useAuthStore';
import { MAX_FILE_SIZE, formatFileSize } from '../../services/files/fileUtils';
import {
  CIME_DOSSIER_FILE_DRAG,
  dropContainsOsFiles,
  dropIsInternalFile,
  parseInternalFileDrag,
} from '../../lib/fileDrop';
import { FileTreeDropContext } from './FileTreeDropContext';

interface Props {
  dossierId: string;
  folderId: string | null;
  disabled?: boolean;
  onMoveFile?: (fileId: string, fromFolderId: string | null, toFolderId: string | null) => void;
  onUploadedToFolder?: (folderId: string) => void;
  /** Sans la ligne « Glissez un fichier… / Importer » : la page pose son propre bouton. */
  bare?: boolean;
  /** Reçoit la fonction qui ouvre le sélecteur de fichiers (bouton « Importer » de la page). */
  openRef?: MutableRefObject<(() => void) | null>;
  children: ReactNode;
}

interface PendingUpload {
  id: string;
  filename: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

function dataTransferOf(event: DropEvent): DataTransfer | null {
  if ('dataTransfer' in event && event.dataTransfer) return event.dataTransfer;
  return null;
}

export function FileDropzone({
  dossierId,
  folderId,
  disabled,
  onMoveFile,
  onUploadedToFolder,
  bare,
  openRef,
  children,
}: Props) {
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);
  const upload = useUploadFile(dossierId);
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [osDragOver, setOsDragOver] = useState(false);

  const uploadFiles = useCallback(
    async (accepted: File[], targetFolderId: string | null) => {
      if (!collaboratorId) return;
      for (const file of accepted) {
        const id = crypto.randomUUID();
        if (file.size > MAX_FILE_SIZE) {
          setPending((p) => [...p, {
            id, filename: file.name, status: 'error',
            error: `Trop volumineux (${formatFileSize(file.size)}, max 50 Mo)`,
          }]);
          continue;
        }
        setPending((p) => [...p, { id, filename: file.name, status: 'uploading' }]);
        try {
          await upload.mutateAsync({ file, uploaderId: collaboratorId, folderId: targetFolderId });
          setPending((p) => p.map((u) => u.id === id ? { ...u, status: 'done' } : u));
          if (targetFolderId) onUploadedToFolder?.(targetFolderId);
          setTimeout(() => setPending((p) => p.filter((u) => u.id !== id)), 2000);
        } catch (e) {
          setPending((p) => p.map((u) => u.id === id ? {
            ...u, status: 'error', error: e instanceof Error ? e.message : 'Erreur',
          } : u));
        }
      }
    },
    [collaboratorId, upload, onUploadedToFolder],
  );

  const onDrop = useCallback(
    async (accepted: File[], _rej: unknown, event: DropEvent) => {
      const dt = dataTransferOf(event);
      const types = dt ? Array.from(dt.types) : [];
      if (dt && dropIsInternalFile(types)) {
        const payload = parseInternalFileDrag(
          dt.getData(CIME_DOSSIER_FILE_DRAG) || dt.getData('text/plain'),
        );
        if (payload) onMoveFile?.(payload.fileId, payload.fromFolderId, folderId);
        return;
      }
      await uploadFiles(accepted, folderId);
    },
    [folderId, onMoveFile, uploadFiles],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    disabled,
    noClick: true,
    noKeyboard: true,
    onDragEnter: (e) => {
      setOsDragOver(dropContainsOsFiles(Array.from(e.dataTransfer.types)));
    },
    onDragLeave: () => setOsDragOver(false),
    onDropAccepted: () => setOsDragOver(false),
    onDropRejected: () => setOsDragOver(false),
  });

  const showRootOsHint = !disabled && isDragActive && osDragOver;

  useEffect(() => {
    if (!openRef) return;
    openRef.current = open;
    return () => {
      openRef.current = null;
    };
  }, [openRef, open]);

  return (
    <FileTreeDropContext.Provider
      value={{
        enabled: !disabled,
        onInternalDrop: (fileId, fromFolderId, toFolderId) => {
          onMoveFile?.(fileId, fromFolderId, toFolderId);
        },
        onOsDrop: (targetId, files) => {
          void uploadFiles(files, targetId);
        },
      }}
    >
      <div
        {...getRootProps()}
        className={'relative ' + (bare ? 'rounded-xl ' : 'rounded-md ') + (showRootOsHint ? 'ring-2 ring-cime-primary ring-offset-1' : '')}
      >
        <input {...getInputProps()} />

        {!disabled && !bare && (
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-xs text-neutral-500">
              {showRootOsHint
                ? 'Déposez à la racine, ou sur un sous-dossier.'
                : 'Glissez un fichier vers un sous-dossier, ou'}
              {!showRootOsHint && (
                <button
                  type="button"
                  onClick={open}
                  className="ml-1 text-cime-primary hover:underline"
                >
                  parcourez votre ordinateur
                </button>
              )}
            </p>
            <button
              type="button"
              onClick={open}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-neutral-200 hover:bg-neutral-50"
            >
              <Upload className="w-4 h-4" /> Importer
            </button>
          </div>
        )}

        {pending.length > 0 && (
          <div className="mb-3 space-y-1.5">
            {pending.map((u) => (
              <div
                key={u.id}
                className={
                  'flex items-center justify-between text-xs px-3 py-1.5 rounded-md border ' +
                  (u.status === 'error' ? 'border-red-200 bg-red-50 text-red-700'
                   : u.status === 'done' ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                   : 'border-neutral-200 bg-neutral-50 text-neutral-700')
                }
              >
                <span className="truncate">{u.filename}</span>
                <span>
                  {u.status === 'uploading' && 'Envoi…'}
                  {u.status === 'done' && '✓ Envoyé'}
                  {u.status === 'error' && (u.error ?? 'Erreur')}
                </span>
              </div>
            ))}
          </div>
        )}

        {children}
      </div>
    </FileTreeDropContext.Provider>
  );
}
